// Auth store (§9.3 boot step 3 + §9.4 role routing). Holds the signed-in
// user (`GET /v1/auth/me` shape) and drives the router guard. The access
// token itself is NOT kept here — it lives in `../lib/session` (in-memory
// only, never persisted); this store just orchestrates login/refresh/logout
// and exposes the user + derived routing helpers.
import { defineStore } from 'pinia';
import { EnrollmentStatus, Role } from '@eskayscience/shared/enums';
import type { AuthMeResponse, LoginResponse } from '@eskayscience/shared/dto';

import { authApi, ApiError } from '@/lib/api';
import {
  clearAccessToken,
  clearSessionSeen,
  getAccessToken,
  hadSessionBefore,
  markSessionSeen,
  setAccessToken,
} from '@/lib/session';
import { commandPoller } from '@/lib/command-poll';
import { createCommandHandlers } from '@/lib/command-handlers';
import { clearRecentSims } from '@/lib/recent-sims';
import { queryClient } from '@/lib/query-client';

export type UserRole = Role;
/** Kept for callers that imported the old shape from this module. */
export type AuthUser = AuthMeResponse;

type AuthStatus = 'idle' | 'loading' | 'ready' | 'error';

/** Route name of the shell a given role lands in after auth (§9.2/§9.4):
 * ADMIN + TEACHER share the authoring shell, STUDENT gets the learn shell. */
export function homeRouteForRole(role: UserRole): 'teacher-shell' | 'learn-shell' {
  return role === Role.STUDENT ? 'learn-shell' : 'teacher-shell';
}

/** A student whose enrollments have all lapsed (≥1 level, none ACTIVE) is
 * "expired" and gets routed to `/app/learn/expired` (§9.3 step 4). An empty
 * summary (the Phase-0 stub, or a brand-new student) is NOT treated as
 * expired — we don't lock anyone out before enrollment resolution exists. */
export function isEntitlementExpired(user: AuthMeResponse | null): boolean {
  if (!user || user.role !== Role.STUDENT) return false;
  const levels = user.entitlementSummary?.levels ?? [];
  if (levels.length === 0) return false;
  return !levels.some((level) => level.status === EnrollmentStatus.ACTIVE);
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as AuthMeResponse | null,
    status: 'idle' as AuthStatus,
  }),
  getters: {
    isAuthenticated: (state): boolean => state.user !== null,
    role: (state): UserRole | null => state.user?.role ?? null,
    homeRoute: (state): 'teacher-shell' | 'learn-shell' | null =>
      state.user ? homeRouteForRole(state.user.role) : null,
    entitlementExpired: (state): boolean => isEntitlementExpired(state.user),
  },
  actions: {
    /** Adopt a freshly issued session (login / verify-email / invite-accept
     * all return the same `LoginResponse` envelope): stash the access token
     * in memory and set the user. Also starts the P2-25 command poller
     * (design §5.1: "after authStore.applySession() ... only ever when
     * authStore.isAuthenticated") — every entry point that establishes a
     * session goes through this one method, so this is the single place
     * that needs to start it for login/verify-email/invite-accept. */
    applySession(res: LoginResponse): void {
      // FE-C: clear any query cache left over from a PRIOR session before
      // adopting this one — without a page reload, a second login in the
      // same tab (e.g. sign-out then straight back in as someone else) would
      // otherwise keep serving the previous user's cached query results
      // until each query naturally refetches. The server remains the sole
      // authority either way; this only prevents stale-data bleed in the UI.
      // LOW-3: cancel in-flight queries BEFORE clearing — otherwise a query
      // that was already in flight from the prior session can resolve AFTER
      // `clear()` and repopulate the cache via a still-mounted observer,
      // leaking the ending session's data into the new one.
      void queryClient.cancelQueries();
      queryClient.clear();
      setAccessToken(res.accessToken);
      markSessionSeen();
      this.user = res.user;
      this.status = 'ready';
      this.startCommandPoller();
    },

    /** Begin (or restart) the P2-25 command poller with the real dispatch
     * handlers (`../lib/command-handlers`). Idempotent — `CommandPoller.start`
     * stops any previous run first. Split out so both `applySession` (login /
     * verify-email / invite-accept) and `boot/index.ts` (cold-load session
     * recovery) can call the exact same wiring. */
    startCommandPoller(): void {
      commandPoller.start(createCommandHandlers(), { refresh: () => this.refresh() });
    },

    /** Local-only teardown of the in-memory session, WITHOUT calling the
     * server logout endpoint — used by the P2-25 `FORCE_LOGOUT` /
     * `SESSION_SUPERSEDED` / `AUTH_TOKEN_EXPIRED` dispatch handlers
     * (`../lib/command-handlers`), which fire a best-effort, non-awaited
     * `authApi.logout()` themselves (design §5.4) rather than going through
     * the full `logout()` action below (which DOES await the server call and
     * would delay the redirect). */
    resetSession(): void {
      // Clear the P3-FE-03 client-local recency overlay for the user who is
      // being signed out — must happen BEFORE `this.user = null` below, and
      // on every path that reaches this method (user-initiated logout below,
      // and the P2-25 FORCE_LOGOUT/SESSION_SUPERSEDED teardown in
      // `../lib/command-handlers.ts`), so a shared lab machine never leaks
      // one student's sim history to the next.
      if (this.user) {
        clearRecentSims(this.user.id);
      }
      clearAccessToken();
      clearSessionSeen();
      this.user = null;
      this.status = 'idle';
      // FE-C: drop every cached query result along with the session — this
      // must run on EVERY path that ends a session (user-initiated logout
      // below, and the P2-25 FORCE_LOGOUT/SESSION_SUPERSEDED/AUTH_TOKEN_EXPIRED
      // teardown in `../lib/command-handlers.ts`) so a shared machine or a
      // superseded session never leaks one user's cached data to the next.
      // LOW-3: cancel in-flight queries first — a query still in flight when
      // the session ends can otherwise resolve AFTER `clear()` and
      // repopulate the cache (a still-mounted observer accepts the late
      // resolve), leaking the ending session's data past the teardown.
      void queryClient.cancelQueries();
      queryClient.clear();
    },

    async login(email: string, password: string): Promise<AuthMeResponse> {
      const res = await authApi.login({ email, password });
      this.applySession(res);
      return res.user;
    },

    /** Refresh the access token from the HttpOnly refresh cookie. Returns
     * `true` on success. Never throws for the expected "no/expired cookie"
     * case — a 401 just means "no live session", which callers treat as
     * signed-out.
     *
     * Thin wrapper over `refreshWithUser()` for callers (e.g. the P2-25
     * command poller) that only care about success/failure, not whether
     * THIS response carried `user` — see `refreshWithUser()` for why that
     * distinction matters and must not be inferred from `this.user`. */
    async refresh(): Promise<boolean> {
      const { ok } = await this.refreshWithUser();
      return ok;
    },

    /** Same as `refresh()`, but also reports whether THIS response actually
     * carried `user` — as opposed to `this.user` merely being truthy, which
     * could be a stale leftover from a PRIOR call/session and would wrongly
     * suggest the current response populated it (MED-1). Callers that need
     * to decide whether a follow-up `fetchMe()` is required (i.e.
     * `initSession()`'s cold-boot path) must branch on `userLoaded` here,
     * never on the ambient `this.user` store field. */
    async refreshWithUser(): Promise<{ ok: boolean; userLoaded: boolean }> {
      try {
        const res = await authApi.refresh();
        setAccessToken(res.accessToken);
        markSessionSeen();
        // If the server included the user in this response (the common case
        // now), adopt it here so a cold-boot caller can skip the separate
        // `/me` round trip below. Older/edge-case response shapes without
        // `user` are handled gracefully by callers still falling back to
        // `fetchMe()` — this never assumes `user` is present.
        if (res.user) {
          this.user = res.user;
          return { ok: true, userLoaded: true };
        }
        return { ok: true, userLoaded: false };
      } catch (err) {
        if (err instanceof ApiError) return { ok: false, userLoaded: false };
        throw err;
      }
    },

    /** Load the current user with whatever access token is in memory. */
    async fetchMe(): Promise<AuthMeResponse | null> {
      try {
        this.user = await authApi.me();
        return this.user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          this.user = null;
          return null;
        }
        throw err;
      }
    },

    /**
     * Boot-time session bootstrap (§9.3 step 3). On a true cold load there is
     * no in-memory access token yet (`../lib/session` holds it only in a
     * module-scoped variable, lost on reload) — a `GET /me` would be
     * guaranteed to 401 first, so we skip straight to ONE silent
     * `POST /refresh` (the HttpOnly cookie may still be valid). `refresh()`
     * now adopts an inline `user` from that response itself, so if it's
     * present we're done — no follow-up `/me` at all. Only when a successful
     * refresh did NOT come back with `user` (an older cached response shape,
     * or any other edge case) do we fall back to a single `/me`. This
     * removes a doomed-to-fail first round trip, and often the second one
     * too, without changing the end state or weakening validation: the
     * server is still the one deciding whether the session is live either
     * way.
     *
     * If an in-memory access token IS already present when this runs (not
     * the normal boot path today, but kept for safety / future callers),
     * fall back to the original probe: `/me` first, and only fall through to
     * refresh on a clean 401 — this is the path that also covers a
     * mid-session token-expiry retry, so it must keep validating via `/me`
     * rather than assuming the in-memory token is still good.
     *
     * Any failure leaves `user = null` and boot proceeds — the router guard
     * sends the visitor to login.
     *
     * A browser that has never established a session (no `../lib/session`
     * "session-seen" marker) skips the probe entirely (PERF-03): both calls
     * are guaranteed 401s for a true first-time/anonymous visitor, and the
     * browser's network layer logs those to the console regardless of JS
     * error handling — this is the `errors-in-console` Lighthouse finding on
     * a fresh `/`/`/login` visit. Anyone who HAS signed in before still gets
     * the full probe (refresh always re-validates against the server, which
     * remains the sole authority), so behaviour/security for real users is
     * unchanged.
     */
    async initSession(): Promise<void> {
      this.status = 'loading';
      if (!hadSessionBefore()) {
        this.user = null;
        this.status = 'ready';
        return;
      }
      try {
        if (getAccessToken() === null) {
          // True cold boot: no in-memory token, so `/me` would just 401.
          // Go straight to the silent refresh. `refreshWithUser()` now adopts
          // `res.user` itself when the server includes it, so a successful
          // refresh that populated it THIS call can skip the separate `/me`
          // round trip entirely. We branch on `userLoaded` — whether THIS
          // response carried `user` — rather than on `this.user`, which
          // could be a stale leftover from a prior session/call and would
          // wrongly skip `fetchMe()` even when the current refresh didn't
          // populate anything (MED-1). If the response didn't include
          // `user` (older cached response shape, or any other edge case),
          // fall back to `fetchMe()` exactly as before.
          const { ok, userLoaded } = await this.refreshWithUser();
          if (ok && !userLoaded) {
            await this.fetchMe();
          }
          this.status = 'ready';
          return;
        }
        const user = await this.fetchMe();
        if (user) {
          this.status = 'ready';
          return;
        }
        // /me was a clean 401 → try one silent refresh, then retry /me.
        if (await this.refresh()) {
          await this.fetchMe();
        }
        this.status = 'ready';
      } catch (err) {
        // Network error or unexpected failure — never block boot; treat as
        // signed out. (A genuine 401 was already handled above.)
        console.warn('[auth] session bootstrap failed, treating as signed out:', err);
        this.user = null;
        this.status = 'error';
      }
    },

    async logout(): Promise<void> {
      commandPoller.stop();
      try {
        await authApi.logout();
      } catch {
        // Logout is best-effort client-side; even if the server call fails
        // (offline), we still drop local session state below.
      }
      this.resetSession();
    },
  },
});
